<template>
  <div class="chat-widget-container" v-if="showWidget">
    <transition name="widget-slide">
      <div v-if="isOpen" class="chat-widget-panel" :style="panelStyle">
        <div class="widget-resize-left" @mousedown.prevent="startResize('left')"></div>
        <div class="widget-resize-top" @mousedown.prevent="startResize('top')"></div>
        <div class="widget-resize-corner" @mousedown.prevent="startResize('corner')"></div>

        <div class="widget-header">
          <span class="widget-title">Cyanic Assistant</span>
          <div class="widget-header-actions">
            <button class="widget-btn-expand" @click="openFullView" title="Open full view">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4h4M16 4h4v4M4 16v4h4M16 20h4v-4"/>
              </svg>
            </button>
            <button class="widget-btn-new" @click="startNewChat" title="New conversation">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
              </svg>
            </button>
            <button class="widget-btn-close" @click="closeWidget" title="Close">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="widget-messages" ref="widgetMessages">
          <div v-if="messages.length === 0 && !streaming" class="widget-welcome">
            <div class="widget-welcome-icon">
              <svg width="40" height="40" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z"/>
              </svg>
            </div>
            <p class="widget-welcome-text">Ask me about tenant security, billing, or compliance posture.</p>
          </div>

          <div
            v-for="(msg, i) in messages"
            :key="i"
            class="widget-message"
            :class="'msg-' + msg.role"
          >
            <div v-if="msg.role === 'user'" class="widget-bubble widget-bubble-user">
              {{ msg.content }}
            </div>
            <div v-else-if="msg.role === 'assistant' && msg.content" class="widget-bubble widget-bubble-assistant markdown-body" v-html="renderMarkdown(msg.content)">
            </div>
            <div v-if="msg.toolCalls && msg.toolCalls.length" class="widget-tool-chips">
              <div
                v-for="tc in msg.toolCalls.slice(0, 3)"
                :key="tc.id"
                class="widget-tool-chip"
                :class="{ 'tool-err': tc._error, 'tool-ok': !tc._error }"
              >
                <span class="tool-mark">{{ tc._error ? '!' : String.fromCharCode(10003) }}</span>
                {{ tc._label || tc.name }}
              </div>
              <span v-if="msg.toolCalls.length > 3" class="widget-tool-more">+{{ msg.toolCalls.length - 3 }} more</span>
            </div>
          </div>

          <div v-if="streaming" class="widget-message msg-assistant">
            <div class="widget-bubble widget-bubble-assistant">
              <span v-if="streamingContent" class="markdown-body" v-html="renderMarkdown(streamingContent)"></span>
              <span v-else class="widget-typing">
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
              </span>
            </div>
            <div v-if="activeToolCalls.length" class="widget-tool-chips">
              <div v-for="tc in activeToolCalls" :key="tc.id" class="widget-tool-chip tool-pending">
                <span class="tool-spinner"></span>
                {{ tc.label }}
              </div>
            </div>
          </div>
        </div>

        <div class="widget-input-area">
          <input
            ref="widgetInput"
            v-model="inputText"
            class="widget-input"
            placeholder="Ask about tenants, billing, security..."
            @keydown.enter.exact.prevent="sendMessage"
            :disabled="streaming"
          />
          <button class="widget-btn-send" @click="sendMessage" :disabled="streaming || !inputText.trim()">
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
            </svg>
          </button>
        </div>
      </div>
    </transition>

    <button class="chat-widget-fab" @click="toggleWidget" :class="{ 'fab-open': isOpen }" title="Cyanic Assistant">
      <svg v-if="!isOpen" width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z"/>
      </svg>
      <svg v-else width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>
  </div>
</template>

<script>
import { streamChatMessage } from '@/services/chat-api.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

marked.setOptions({ breaks: true, gfm: true });

export default {
  name: 'ChatWidget',
  data() {
    return {
      isOpen: false,
      messages: [],
      activeSessionId: null,
      inputText: '',
      streaming: false,
      streamingContent: '',
      activeToolCalls: [],
      panelWidth: 380,
      panelHeight: 520,
      resizing: null
    };
  },
  computed: {
    showWidget() {
      return this.$route.meta.requiresAuth === true && this.$route.path !== '/chat';
    },
    panelStyle() {
      return {
        width: this.panelWidth + 'px',
        height: this.panelHeight + 'px'
      };
    }
  },
  mounted() {
    const saved = localStorage.getItem('chatWidgetSize');
    if (saved) {
      try {
        const dims = JSON.parse(saved);
        if (dims.width && dims.height) {
          this.panelWidth = dims.width;
          this.panelHeight = dims.height;
        }
      } catch (e) { /* ignore */ }
    }
  },
  methods: {
    startResize(dir) {
      this.resizing = {
        dir,
        startX: event.clientX,
        startY: event.clientY,
        startW: this.panelWidth,
        startH: this.panelHeight
      };
      document.addEventListener('mousemove', this.onResizeMove);
      document.addEventListener('mouseup', this.stopResize);
    },
    onResizeMove(event) {
      if (!this.resizing) return;
      const minW = 320;
      const minH = 360;
      const maxW = Math.min(window.innerWidth - 48, 900);
      const maxH = window.innerHeight - 120;
      if (this.resizing.dir === 'left' || this.resizing.dir === 'corner') {
        const dx = this.resizing.startX - event.clientX;
        this.panelWidth = Math.max(minW, Math.min(maxW, this.resizing.startW + dx));
      }
      if (this.resizing.dir === 'top' || this.resizing.dir === 'corner') {
        const dy = this.resizing.startY - event.clientY;
        this.panelHeight = Math.max(minH, Math.min(maxH, this.resizing.startH + dy));
      }
    },
    stopResize() {
      if (this.resizing) {
        localStorage.setItem('chatWidgetSize', JSON.stringify({
          width: this.panelWidth,
          height: this.panelHeight
        }));
        this.resizing = null;
      }
      document.removeEventListener('mousemove', this.onResizeMove);
      document.removeEventListener('mouseup', this.stopResize);
    },
    toggleWidget() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => this.$refs.widgetInput?.focus());
      }
    },
    closeWidget() {
      this.isOpen = false;
    },
    openFullView() {
      this.isOpen = false;
      this.$router.push('/chat');
    },
    startNewChat() {
      this.activeSessionId = null;
      this.messages = [];
      this.streamingContent = '';
      this.activeToolCalls = [];
      this.$nextTick(() => this.$refs.widgetInput?.focus());
    },
    async sendMessage() {
      const text = this.inputText.trim();
      if (!text || this.streaming) return;

      this.messages.push({ role: 'user', content: text });
      this.inputText = '';
      this.streaming = true;
      this.streamingContent = '';
      this.activeToolCalls = [];

      const assistantMsg = { role: 'assistant', content: '', toolCalls: [] };
      this.messages.push(assistantMsg);

      try {
        await streamChatMessage(text, this.activeSessionId, {
          onSession: (data) => {
            this.activeSessionId = data.sessionId;
          },
          onToken: (tokenText) => {
            this.streamingContent += tokenText;
            assistantMsg.content = this.streamingContent;
            this.$nextTick(() => this.scrollToBottom());
          },
          onToolCall: (data) => {
            this.activeToolCalls.push({ id: data.id, label: data.name });
          },
          onToolResult: (data) => {
            const idx = this.activeToolCalls.findIndex(tc => tc.id === data.id);
            if (idx !== -1) this.activeToolCalls.splice(idx, 1);
            assistantMsg.toolCalls.push({
              id: data.id,
              name: data.name,
              _label: data.summary,
              _error: !data.ok
            });
          },
          onDone: () => {
            this.streaming = false;
            this.activeToolCalls = [];
            this.$nextTick(() => this.$refs.widgetInput?.focus());
          },
          onError: (data) => {
            assistantMsg.content = `**Error:** ${data.message}`;
            this.streaming = false;
            this.activeToolCalls = [];
          }
        });
      } catch (e) {
        assistantMsg.content = `**Error:** ${e.message}`;
        this.streaming = false;
        this.activeToolCalls = [];
      }
    },
    renderMarkdown(text) {
      if (!text) return '';
      return DOMPurify.sanitize(marked.parse(text));
    },
    scrollToBottom() {
      const el = this.$refs.widgetMessages;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }
};
</script>

<style scoped>
.chat-widget-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  font-family: 'Inter', -apple-system, sans-serif;
}

.chat-widget-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #4f46e5;
  color: #fff;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.chat-widget-fab:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.5);
}

.chat-widget-fab.fab-open {
  background: #64748b;
  box-shadow: 0 4px 12px rgba(100, 116, 139, 0.4);
}

.chat-widget-panel {
  position: absolute;
  bottom: 70px;
  right: 0;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.widget-resize-left {
  position: absolute;
  left: 0;
  top: 0;
  width: 6px;
  height: 100%;
  cursor: ew-resize;
  z-index: 10;
}

.widget-resize-top {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 6px;
  cursor: ns-resize;
  z-index: 10;
}

.widget-resize-corner {
  position: absolute;
  left: 0;
  top: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  z-index: 11;
}

.widget-header {
  background: #4f46e5;
  color: #fff;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.widget-title {
  font-size: 14px;
  font-weight: 600;
}

.widget-header-actions {
  display: flex;
  gap: 4px;
}

.widget-btn-expand,
.widget-btn-new,
.widget-btn-close {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.widget-btn-expand:hover,
.widget-btn-new:hover,
.widget-btn-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.widget-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.widget-welcome {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #94a3b8;
  text-align: center;
  padding: 24px;
}

.widget-welcome-icon {
  color: #c7d2fe;
}

.widget-welcome-text {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.widget-message {
  display: flex;
  flex-direction: column;
}

.widget-bubble {
  max-width: 85%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  word-wrap: break-word;
}

.widget-bubble-user {
  align-self: flex-end;
  background: #4f46e5;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.widget-bubble-assistant {
  align-self: flex-start;
  background: #fff;
  color: #1e293b;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

.widget-bubble-assistant :deep(p) {
  margin: 0 0 6px 0;
}

.widget-bubble-assistant :deep(p:last-child) {
  margin-bottom: 0;
}

.widget-bubble-assistant :deep(ul),
.widget-bubble-assistant :deep(ol) {
  margin: 4px 0 4px 18px;
  padding: 0;
}

.widget-bubble-assistant :deep(li) {
  margin-bottom: 2px;
}

.widget-bubble-assistant :deep(table) {
  border-collapse: collapse;
  font-size: 12px;
  margin: 6px 0;
}

.widget-bubble-assistant :deep(th),
.widget-bubble-assistant :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 4px 8px;
}

.widget-bubble-assistant :deep(th) {
  background: #f1f5f9;
  font-weight: 600;
}

.widget-bubble-assistant :deep(code) {
  background: #f1f5f9;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 12px;
}

.widget-tool-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.widget-tool-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
}

.widget-tool-chip.tool-ok {
  background: #dcfce7;
  color: #166534;
}

.widget-tool-chip.tool-err {
  background: #fee2e2;
  color: #991b1b;
}

.widget-tool-chip.tool-pending {
  background: #fef3c7;
  color: #92400e;
}

.widget-tool-more {
  font-size: 11px;
  color: #94a3b8;
  align-self: center;
}

.tool-mark {
  font-weight: 700;
  font-size: 10px;
}

.tool-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid #fbbf24;
  border-top-color: transparent;
  border-radius: 50%;
  animation: widget-spin 0.8s linear infinite;
}

@keyframes widget-spin {
  to { transform: rotate(360deg); }
}

.widget-typing {
  display: inline-flex;
  gap: 4px;
  padding: 4px 0;
}

.typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #c7d2fe;
  animation: widget-bounce 1.4s ease-in-out infinite;
}

.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes widget-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}

.widget-input-area {
  padding: 8px 12px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.widget-input {
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 13px;
  outline: none;
  font-family: inherit;
  color: #1e293b;
  background: #f9fafb;
}

.widget-input:focus {
  border-color: #4f46e5;
  background: #fff;
}

.widget-input::placeholder {
  color: #94a3b8;
}

.widget-btn-send {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #4f46e5;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
}

.widget-btn-send:hover:not(:disabled) {
  background: #4338ca;
}

.widget-btn-send:disabled {
  background: #c7d2fe;
  cursor: not-allowed;
}

.widget-slide-enter-active,
.widget-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.widget-slide-enter-from,
.widget-slide-leave-to {
  transform: translateY(20px) scale(0.95);
  opacity: 0;
}

.widget-messages::-webkit-scrollbar {
  width: 5px;
}

.widget-messages::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.widget-messages::-webkit-scrollbar-track {
  background: transparent;
}

@media (max-width: 640px) {
  .chat-widget-panel {
    width: calc(100vw - 32px) !important;
    height: calc(100vh - 120px) !important;
  }
}
</style>
